/**
 * Agent detail page — FE-22.
 * Info card, Restrict/Restore actions, governance timeline, reports, H2A stats.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  type AgentDetail,
  type AgentReport,
  type AuditEntry,
  type GovernanceStatus,
  fetchAgentDetail,
  restoreAgent,
  restrictAgent,
} from "../../api/admin-agents";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import {
  Td,
  Th,
  fmtDate,
  shortId,
  tableStyles,
} from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";

const TABS = ["Overview", "Governance History", "Reports"] as const;
type Tab = (typeof TABS)[number];
const TAB_KEYS: Record<Tab, string> = {
  Overview: "agent_detail.tab_overview",
  "Governance History": "agent_detail.tab_governance_history",
  Reports: "agent_detail.tab_reports",
};

export function AgentDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [agent, setAgent] = useState<AgentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("Overview");
  const [dialog, setDialog] = useState<"restrict" | "restore" | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setAgent(await fetchAgentDetail(id));
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t("agent_detail.failed_to_load"),
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConfirm = useCallback(
    async (reason: string): Promise<void> => {
      if (!id || !dialog) return;
      try {
        if (dialog === "restrict") await restrictAgent(id, reason);
        else await restoreAgent(id, reason);
        setDialog(null);
        void load();
      } catch (e) {
        setError(e instanceof Error ? e.message : t("common.action_failed"));
        setDialog(null);
      }
    },
    [id, dialog, load],
  );

  if (loading) return <p style={s.msg}>{t("agent_detail.loading")}</p>;
  if (error)
    return <p style={{ ...s.msg, color: "var(--color-danger)" }}>{error}</p>;
  if (!agent) return <p style={s.msg}>{t("agent_detail.not_found")}</p>;

  return (
    <div>
      <PageHeader
        title={agent.name}
        description={t("agent_detail.created_by", { name: agent.creator_name })}
        actions={
          <ActionButtons
            status={agent.governance_status}
            onAction={setDialog}
          />
        }
      />
      <InfoCard agent={agent} />
      <H2aStatsBar stats={agent.h2a_stats} />
      <TabBar active={tab} onChange={setTab} />
      <TabContent tab={tab} agent={agent} />
      <ConfirmDialog
        open={dialog !== null}
        title={
          dialog === "restrict"
            ? t("agent_detail.restrict_title")
            : t("agent_detail.restore_title")
        }
        description={
          dialog === "restrict"
            ? t("agent_detail.restrict_desc")
            : t("agent_detail.restore_desc")
        }
        reasonRequired
        reasonMinLength={10}
        onConfirm={(r) => void handleConfirm(r)}
        onCancel={() => setDialog(null)}
      />
    </div>
  );
}

// ─── Action buttons ───

function ActionButtons(props: {
  status: GovernanceStatus;
  onAction: (a: "restrict" | "restore") => void;
}): React.JSX.Element {
  if (props.status === "banned") {
    return <span style={s.bannedLabel}>{t("agent_detail.banned")}</span>;
  }
  if (props.status === "active") {
    return (
      <button
        type="button"
        style={s.dangerBtn}
        onClick={() => props.onAction("restrict")}
      >
        Restrict Agent
      </button>
    );
  }
  return (
    <button
      type="button"
      style={s.primaryBtn}
      onClick={() => props.onAction("restore")}
    >
      Restore Agent
    </button>
  );
}

// ─── Info card ───

function InfoCard({ agent }: { agent: AgentDetail }): React.JSX.Element {
  const mono = { ...s.infoValue, fontFamily: "var(--font-mono)", fontSize: 12 };
  return (
    <div style={s.card}>
      <InfoRow
        label={t("agent_detail.agent_id")}
        value={<span style={mono}>{agent.id}</span>}
      />
      <InfoRow
        label={t("agent_detail.status")}
        value={<StatusBadge status={agent.governance_status} />}
      />
      <InfoRow
        label={t("agent_detail.connection")}
        value={agent.connection_mode}
      />
      <InfoRow
        label={t("agent_detail.risk_score")}
        value={String(agent.risk_score)}
      />
      <InfoRow
        label={t("agent_detail.reports")}
        value={String(agent.report_count)}
      />
      <InfoRow
        label={t("agent_detail.description")}
        value={agent.description ?? "-"}
      />
      <InfoRow
        label={t("agent_detail.created")}
        value={fmtDate(agent.created_at)}
      />
    </div>
  );
}

function InfoRow(props: {
  label: string;
  value: React.ReactNode;
}): React.JSX.Element {
  return (
    <div style={s.infoRow}>
      <span style={s.infoLabel}>{props.label}</span>
      <span style={s.infoValue}>{props.value}</span>
    </div>
  );
}

// ─── H2A stats ───

function H2aStatsBar({
  stats,
}: { stats: AgentDetail["h2a_stats"] }): React.JSX.Element {
  return (
    <div style={s.statsGrid}>
      <StatCard
        label={t("agent_detail.h2a_active")}
        value={stats.active}
        color="#166534"
      />
      <StatCard
        label={t("agent_detail.h2a_suspended")}
        value={stats.suspended}
        color="#991b1b"
      />
      <StatCard
        label={t("agent_detail.h2a_terminated")}
        value={stats.terminated}
        color="#6b7280"
      />
    </div>
  );
}

function StatCard(props: {
  label: string;
  value: number;
  color: string;
}): React.JSX.Element {
  return (
    <div style={s.statCard}>
      <span style={s.statLabel}>{props.label}</span>
      <span style={{ ...s.statValue, color: props.color }}>{props.value}</span>
    </div>
  );
}

// ─── Tabs ───

function TabBar(props: {
  active: Tab;
  onChange: (t: Tab) => void;
}): React.JSX.Element {
  return (
    <div style={s.tabBar}>
      {TABS.map((tb) => (
        <button
          key={tb}
          type="button"
          onClick={() => props.onChange(tb)}
          style={tb === props.active ? { ...s.tab, ...s.tabActive } : s.tab}
        >
          {t(TAB_KEYS[tb])}
        </button>
      ))}
    </div>
  );
}

function TabContent(props: {
  tab: Tab;
  agent: AgentDetail;
}): React.JSX.Element {
  if (props.tab === "Governance History") {
    return <AuditTimeline records={props.agent.audit_records} />;
  }
  if (props.tab === "Reports") {
    return <ReportsTable reports={props.agent.reports} />;
  }
  return <OverviewTab agent={props.agent} />;
}

// ─── Overview tab ───

function OverviewTab({ agent }: { agent: AgentDetail }): React.JSX.Element {
  return (
    <div style={s.statsGrid}>
      {(
        [
          [t("agent_detail.connection_mode"), agent.connection_mode],
          [t("agent_detail.risk_score"), String(agent.risk_score)],
          [t("agent_detail.report_count"), String(agent.report_count)],
          [t("agent_detail.last_updated"), fmtDate(agent.updated_at)],
        ] as const
      ).map(([label, value]) => (
        <div key={label} style={s.statCard}>
          <span style={s.statLabel}>{label}</span>
          <span style={s.statValue}>{value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Governance timeline ───

function AuditTimeline(props: {
  records: ReadonlyArray<AuditEntry>;
}): React.JSX.Element {
  if (props.records.length === 0) {
    return <p style={s.emptyTab}>{t("agent_detail.no_governance_history")}</p>;
  }
  return (
    <div style={s.timeline}>
      {props.records.map((r) => (
        <div key={r.id} style={s.timelineItem}>
          <div style={s.timelineDot} />
          <div style={s.timelineContent}>
            <span style={s.timelineAction}>{r.action}</span>
            <span style={s.timelineMeta}>
              by {shortId(r.actor_id)} &middot; {fmtDate(r.created_at)}
            </span>
            {r.reason && <p style={s.timelineReason}>{r.reason}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Reports table ───

function ReportsTable(props: {
  reports: ReadonlyArray<AgentReport>;
}): React.JSX.Element {
  if (props.reports.length === 0) {
    return <p style={s.emptyTab}>{t("agent_detail.no_reports")}</p>;
  }
  return (
    <table style={s.table}>
      <thead>
        <tr>
          <Th>{t("agent_detail.col_reporter")}</Th>
          <Th>{t("agent_detail.col_reason")}</Th>
          <Th>{t("agent_detail.col_status")}</Th>
          <Th>{t("agent_detail.col_date")}</Th>
        </tr>
      </thead>
      <tbody>
        {props.reports.map((r) => (
          <tr key={r.id}>
            <Td mono>{shortId(r.reporter_id)}</Td>
            <Td>{truncate(r.reason, 80)}</Td>
            <Td>{r.status}</Td>
            <Td>{fmtDate(r.created_at)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── StatusBadge ───

function StatusBadge({
  status,
}: { status: GovernanceStatus }): React.JSX.Element {
  const c = GOVERNANCE_COLOR[status];
  return (
    <span style={{ ...s.badge, background: c.bg, color: c.fg }}>
      {capitalize(status)}
    </span>
  );
}

const GOVERNANCE_COLOR: Record<GovernanceStatus, { bg: string; fg: string }> = {
  active: { bg: "#dcfce7", fg: "#166534" },
  restricted: { bg: "#fef9c3", fg: "#854d0e" },
  suspended: { bg: "#fef2f2", fg: "#991b1b" },
  banned: { bg: "#f3f4f6", fg: "#6b7280" },
};

// ─── Helpers ───

function capitalize(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function truncate(str: string, max: number): string {
  return str.length > max ? `${str.slice(0, max)}...` : str;
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  ...tableStyles,
  msg: { fontSize: 14, color: "var(--color-text-secondary)", padding: 24 },
  card: {
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  infoRow: { display: "flex", padding: "6px 0", gap: 12 },
  infoLabel: {
    width: 120,
    fontSize: 12,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
  },
  infoValue: { fontSize: 13 },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
  },
  dangerBtn: {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-danger)",
    color: "#fff",
  },
  primaryBtn: {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-primary)",
    color: "#fff",
  },
  bannedLabel: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
    fontStyle: "italic",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 12,
    marginBottom: 20,
  },
  statCard: {
    display: "flex",
    flexDirection: "column",
    padding: 16,
    border: "1px solid var(--color-border)",
    borderRadius: 8,
  },
  statLabel: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    marginBottom: 4,
  },
  statValue: { fontSize: 18, fontWeight: 600 },
  tabBar: {
    display: "flex",
    gap: 0,
    borderBottom: "1px solid var(--color-border)",
    marginBottom: 16,
  },
  tab: {
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 500,
    background: "none",
    color: "var(--color-text-secondary)",
    borderBottom: "2px solid transparent",
  },
  tabActive: {
    color: "var(--color-text)",
    borderBottomColor: "var(--color-primary)",
  },
  emptyTab: { fontSize: 13, color: "var(--color-text-secondary)", padding: 16 },
  timeline: { paddingLeft: 16 },
  timelineItem: {
    display: "flex",
    gap: 12,
    paddingBottom: 16,
    position: "relative",
  },
  timelineDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "var(--color-primary)",
    marginTop: 5,
    flexShrink: 0,
  },
  timelineContent: { flex: 1 },
  timelineAction: {
    fontSize: 13,
    fontWeight: 600,
    display: "block",
  },
  timelineMeta: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    display: "block",
    marginTop: 2,
  },
  timelineReason: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    marginTop: 4,
    lineHeight: 1.5,
  },
};
